import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { parseCurrency, formatMoney } from '@/lib/currency';

/**
 * Free-text currency input. Accepts naturally-typed values:
 *   5000 · 5,000 · $5,000 · $5,000.50 · "  5000 "
 *
 * Behaviour:
 *  - Renders the raw user text while focused, formatted "$5,000" while blurred.
 *  - Calls onValue(number | null) only when the parsed numeric value changes.
 *  - On invalid input (no digits), shows the inline error and reports null.
 */
const CurrencyInput = ({
  value,
  onValue,
  placeholder = '$0',
  required = false,
  testid,
  className = '',
  allowEmpty = false,
  decimals = false,
  disabled = false,
}) => {
  const [text, setText] = useState(() => (value === null || value === undefined || value === '' ? '' : formatMoney(value, { withDecimals: decimals })));
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState(null);

  // Reflect external value changes (e.g. resetting form) when not focused.
  useEffect(() => {
    if (!focused) {
      setText(value === null || value === undefined || value === '' ? '' : formatMoney(value, { withDecimals: decimals }));
      setError(null);
    }
  }, [value, focused, decimals]);

  const handleChange = (e) => {
    setText(e.target.value);
    setError(null);
  };

  const commit = () => {
    setFocused(false);
    if (text.trim() === '') {
      if (allowEmpty) {
        onValue?.(null);
        setText('');
      } else if (required) {
        setError('Amount is required');
        onValue?.(null);
      } else {
        onValue?.(0);
        setText(formatMoney(0, { withDecimals: decimals }));
      }
      return;
    }
    const parsed = parseCurrency(text);
    if (!Number.isFinite(parsed)) {
      setError('Enter a number — commas and $ are okay');
      onValue?.(null);
      return;
    }
    onValue?.(parsed);
    setText(formatMoney(parsed, { withDecimals: decimals }));
  };

  return (
    <div>
      <Input
        type="text"
        inputMode="decimal"
        value={text}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={commit}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        data-testid={testid}
        className={`border-slate-300 ${error ? 'border-[#C53030]' : ''} ${className}`}
        aria-invalid={!!error}
      />
      {error && (
        <p className="text-xs text-[#C53030] mt-1" data-testid={testid ? `${testid}-error` : undefined}>
          {error}
        </p>
      )}
    </div>
  );
};

export default CurrencyInput;
